<template>
  <div>
    <RctNavbar :links="navLinks" logo-href="https://ridgecodetech.com">
    </RctNavbar>

    <!-- HERO -->
    <section class="hero">
      <div class="hero-content">
        <p class="hero-eyebrow">Web Development · Athens, AL</p>
        <h1 class="hero-headline">Modern web,<br><em>reliably hosted.</em></h1>
        <p class="hero-sub">Custom web development and managed hosting — from edge-deployed applications to managed WordPress. Built right, kept running.</p>
        <div class="hero-actions">
          <RctButton href="#contact">Start a Project</RctButton>
          <RctButton variant="ghost" href="#services">View Services →</RctButton>
        </div>
      </div>
      <div class="ridge-divider">
        <svg viewBox="0 0 1130 120" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="0" y="80" width="1130" height="40" fill="#f5f8ff"/>
          <path d="M0,120 L0,90 C60,88 120,85 180,82 C220,80 260,74 300,72 C340,70 360,68 390,64 C420,60 445,55 470,52 C490,49 505,50 520,53 C538,57 552,62 568,65 C590,69 615,70 640,68 C665,66 690,60 715,56 C740,52 760,50 785,52 C810,54 830,58 855,62 C880,66 905,70 930,73 C960,77 990,80 1020,83 C1060,87 1090,90 1130,92 L1130,120 Z" fill="#f5f8ff"/>
          <path d="M0,90 C60,88 120,85 180,82 C220,80 260,74 300,72 C340,70 360,68 390,64 C420,60 445,55 470,52 C490,49 505,50 520,53 C538,57 552,62 568,65 C590,69 615,70 640,68 C665,66 690,60 715,56 C740,52 760,50 785,52 C810,54 830,58 855,62 C880,66 905,70 930,73 C960,77 990,80 1020,83 C1060,87 1090,90 1130,92" fill="none" stroke="#becdea" stroke-width="1.5" opacity="0.6"/>
        </svg>
      </div>
    </section>

    <!-- SERVICES -->
    <section class="services" id="services">
      <div class="section-inner">
        <p class="section-label reveal">What I Build</p>
        <h2 class="section-heading reveal reveal-delay-1">Full-stack web, front to back.</h2>
        <div class="services-grid">
          <div class="service-card reveal reveal-delay-1">
            <svg class="service-icon" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="3" y="6" width="30" height="24" rx="2" stroke="#7a96c1" stroke-width="1.5"/>
              <path d="M12 16l-4 4 4 4M24 16l4 4-4 4M19 13l-2 10" stroke="#7a96c1" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <h3 class="service-title">Custom Web Development</h3>
            <p class="service-desc">React and Vue applications, REST and GraphQL APIs, full-stack web apps. Greenfield builds and feature additions — no templates, no page builders.</p>
          </div>
          <div class="service-card reveal reveal-delay-2">
            <svg class="service-icon" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="4" y="4" width="28" height="20" rx="2" stroke="#7a96c1" stroke-width="1.5"/>
              <path d="M4 12h28" stroke="#7a96c1" stroke-width="1.5"/>
              <path d="M10 8h2M15 8h2" stroke="#7a96c1" stroke-width="1.5" stroke-linecap="round"/>
              <path d="M10 18h16M10 22h10" stroke="#7a96c1" stroke-width="1.5" stroke-linecap="round"/>
              <rect x="10" y="28" width="16" height="4" rx="1" stroke="#7a96c1" stroke-width="1.5"/>
            </svg>
            <h3 class="service-title">CMS Integration</h3>
            <p class="service-desc">Headless CMS setup (Sanity, Contentful, Strapi) or traditional WordPress — whichever fits the content model and the team managing it.</p>
          </div>
          <div class="service-card reveal reveal-delay-3">
            <svg class="service-icon" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="4" y="8" width="28" height="20" rx="2" stroke="#7a96c1" stroke-width="1.5"/>
              <path d="M4 14h28" stroke="#7a96c1" stroke-width="1.5"/>
              <circle cx="10" cy="11" r="1.5" fill="#7a96c1"/>
              <circle cx="15" cy="11" r="1.5" fill="#7a96c1"/>
              <path d="M12 20l3 3 6-6" stroke="#7a96c1" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <h3 class="service-title">Hosting &amp; Deployment</h3>
            <p class="service-desc">Cloudflare Workers and Pages for modern apps, VPS for full server control, or managed WordPress hosting. Configured, secured, and deployed properly.</p>
          </div>
          <div class="service-card reveal reveal-delay-4">
            <svg class="service-icon" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 4 L32 10 V18 C32 25 25 30 18 32 C11 30 4 25 4 18 V10 Z" stroke="#7a96c1" stroke-width="1.5" stroke-linejoin="round"/>
              <path d="M12 18l4 4 8-8" stroke="#7a96c1" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <h3 class="service-title">Maintenance &amp; Support</h3>
            <p class="service-desc">Ongoing site maintenance, security updates, performance monitoring, and content changes. Monthly retainer or per-incident — whatever the site requires.</p>
          </div>
        </div>
      </div>
    </section>

    <!-- HOSTING -->
    <section class="hosting-section" id="hosting">
      <div class="section-inner">
        <p class="section-label reveal">Hosting Options</p>
        <h2 class="section-heading reveal reveal-delay-1">The right infrastructure<br>for your project.</h2>
        <p class="section-body reveal reveal-delay-2">Hosting isn't one-size-fits-all. The right choice depends on your stack, your traffic, and how much you want to manage. Here's how I approach it.</p>
        <div class="hosting-grid reveal reveal-delay-3">
          <div class="hosting-card">
            <div class="hosting-icon">
              <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 28 C8 28 6 24 10 20 C12 18 15 18 17 19 C18 15 22 12 26 13 C30 14 32 18 31 22 C33 22 35 24 34 27 C33 29 31 30 29 30 H12 C10 30 8 29 8 28Z" stroke="#7a96c1" stroke-width="1.5" stroke-linejoin="round"/>
                <path d="M20 22v8M17 27l3 3 3-3" stroke="#7a96c1" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <h3 class="hosting-title">Cloudflare Edge</h3>
            <p class="hosting-desc">Workers and Pages for modern React/Vue apps and APIs. Globally distributed, zero cold starts, built-in DDoS protection.</p>
            <ul class="hosting-features">
              <li>Serverless — no server to manage</li>
              <li>Global CDN out of the box</li>
              <li>Automatic SSL and DDoS protection</li>
              <li>Best for SPAs, APIs, JAMstack</li>
            </ul>
            <span class="hosting-tag">Recommended for new builds</span>
          </div>
          <div class="hosting-card">
            <div class="hosting-icon">
              <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="6" y="8" width="28" height="10" rx="2" stroke="#7a96c1" stroke-width="1.5"/>
                <rect x="6" y="22" width="28" height="10" rx="2" stroke="#7a96c1" stroke-width="1.5"/>
                <circle cx="30" cy="13" r="2" fill="#7a96c1" opacity="0.6"/>
                <circle cx="30" cy="27" r="2" fill="#7a96c1" opacity="0.6"/>
              </svg>
            </div>
            <h3 class="hosting-title">VPS Hosting</h3>
            <p class="hosting-desc">Full Linux server control for custom stacks, databases, background workers, or anything that doesn't fit a serverless model.</p>
            <ul class="hosting-features">
              <li>Full root access and custom config</li>
              <li>Any stack — Node, Python, Rust, PHP</li>
              <li>Persistent storage and background jobs</li>
              <li>Best for complex or stateful apps</li>
            </ul>
            <span class="hosting-tag">Maximum flexibility</span>
          </div>
          <div class="hosting-card">
            <div class="hosting-icon">
              <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="6" y="10" width="28" height="20" rx="2" stroke="#7a96c1" stroke-width="1.5"/>
                <path d="M6 17h28" stroke="#7a96c1" stroke-width="1.5"/>
                <path d="M13 14h2M18 14h2" stroke="#7a96c1" stroke-width="1.5" stroke-linecap="round"/>
                <path d="M12 23h8M12 27h5" stroke="#7a96c1" stroke-width="1.5" stroke-linecap="round"/>
              </svg>
            </div>
            <h3 class="hosting-title">Managed WordPress</h3>
            <p class="hosting-desc">WordPress hosting with automatic updates, daily backups, security monitoring, and performance tuning — without managing a server.</p>
            <ul class="hosting-features">
              <li>Automatic core and plugin updates</li>
              <li>Daily backups with one-click restore</li>
              <li>Uptime monitoring and alerts</li>
              <li>Best for content-heavy sites</li>
            </ul>
            <span class="hosting-tag">Hands-off operation</span>
          </div>
        </div>
        <p class="hosting-note reveal">Not sure which fits your project? <a href="#contact">Reach out</a> and we'll figure it out together.</p>
      </div>
    </section>

    <!-- ABOUT -->
    <section class="about" id="about">
      <div class="section-inner">
        <p class="section-label reveal">Approach</p>
        <h2 class="section-heading reveal reveal-delay-1">Built to last, not just to launch.</h2>
        <div class="about-layout">
          <div class="about-text reveal reveal-delay-2">
            <p>A lot of web projects launch fine and fall apart six months later — slow performance, a plugin that breaks on update, a hosting bill that doubled. I build and host sites with the full lifecycle in mind.</p>
            <p>That means clean, maintainable code. Infrastructure that's appropriate for the load. CMS choices that match the team managing the content. And hosting that stays configured the way it was set up.</p>
            <p>I work with small businesses, startups, and teams that need a reliable technical partner — not a vendor that disappears after go-live.</p>
          </div>
          <div class="about-stats reveal reveal-delay-3">
            <div class="stat-box">
              <div class="stat-number">Vue</div>
              <div class="stat-label">React, Vue, full-stack TypeScript</div>
            </div>
            <div class="stat-box">
              <div class="stat-number">Edge</div>
              <div class="stat-label">Cloudflare Workers &amp; Pages</div>
            </div>
            <div class="stat-box">
              <div class="stat-number">WP</div>
              <div class="stat-label">WordPress dev and managed hosting</div>
            </div>
            <div class="stat-box">
              <div class="stat-number">CMS</div>
              <div class="stat-label">Sanity, Contentful, Strapi, headless</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CONTACT -->
    <section class="contact-section" id="contact">
      <div class="section-inner">
        <p class="section-label reveal">Support</p>
        <h2 class="section-heading reveal reveal-delay-1">Submit a support ticket.</h2>
        <div class="contact-layout">
          <div class="contact-aside reveal reveal-delay-2">
            <p>Having an issue with your site or hosting? Submit a ticket and I'll respond within one business day. For urgent outages, include "URGENT" in your message.</p>
            <div class="contact-detail">
              <a href="mailto:support@ridgecodetech.com">support@ridgecodetech.com</a>
              <a href="https://ridgecodetech.com">ridgecodetech.com</a>
            </div>
          </div>
          <div class="reveal reveal-delay-3">
            <form class="contact-form" novalidate @submit.prevent="handleSubmit">
              <div class="form-row">
                <div class="form-group">
                  <label for="name">Name</label>
                  <input v-model="formName" type="text" id="name" name="name" placeholder="Your name" required />
                </div>
                <div class="form-group">
                  <label for="email">Email</label>
                  <input v-model="formEmail" type="email" id="email" name="email" placeholder="you@company.com" required />
                </div>
              </div>
              <div class="form-group">
                <label for="service">Issue Type</label>
                <select v-model="formService" id="service" name="service" required>
                  <option value="" disabled>Select an issue type</option>
                  <option value="Site Down / Outage">Site Down / Outage</option>
                  <option value="Performance Issue">Performance Issue</option>
                  <option value="SSL / Domain Issue">SSL / Domain Issue</option>
                  <option value="WordPress Update or Plugin">WordPress Update / Plugin Issue</option>
                  <option value="Content Update">Content Update Request</option>
                  <option value="Billing Question">Billing Question</option>
                  <option value="New Work Request">New Work Request</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div class="form-group">
                <label for="message">Description</label>
                <textarea v-model="formMessage" id="message" name="message" placeholder="Describe the issue — what's happening, when it started, and what you've already tried..." required></textarea>
              </div>
              <div v-if="formStatus.msg" :class="['form-status', formStatus.type]">{{ formStatus.msg }}</div>
              <RctButton type="submit" :loading="formLoading" :disabled="formLoading">Submit Ticket</RctButton>
            </form>
          </div>
        </div>
      </div>
    </section>

    <AgentChat scope="web" :agent-url="AGENT_URL" />

    <RctFooter :links="footerLinks">
    </RctFooter>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { RctNavbar, RctButton, RctFooter, AgentChat } from '@rct/ui';
import { FOOTER_LINKS, WORKER_URL, AGENT_URL, isValidEmail } from '@rct/shared';
import { useAuth } from '../composables/useAuth';

const { isAuthenticated } = useAuth();

const navLinks = computed(() => [
  { href: '#services', label: 'Services' },
  { href: '#hosting',  label: 'Hosting'  },
  { href: '#about',    label: 'Approach' },
  { href: '#contact',  label: 'Support'  },
  isAuthenticated.value
    ? { href: '/dashboard', label: 'Dashboard' }
    : { href: '/sign-in',   label: 'Sign In'   },
]);

const footerLinks = [
  { href: 'https://ridgecodetech.com', label: 'Home' },
  ...FOOTER_LINKS.filter((l) => l.href !== 'https://web.ridgecodetech.com'),
] as { href: string; label: string }[];

const formName    = ref('');
const formEmail   = ref('');
const formService = ref('');
const formMessage = ref('');
const formLoading = ref(false);
const formStatus  = ref<{ type: 'success' | 'error' | ''; msg: string }>({ type: '', msg: '' });

async function handleSubmit() {
  const name    = formName.value.trim();
  const email   = formEmail.value.trim();
  const service = formService.value;
  const message = formMessage.value.trim();

  if (!name || !email || !service || !message) {
    formStatus.value = { type: 'error', msg: 'Please fill in all fields before sending.' };
    return;
  }
  if (!isValidEmail(email)) {
    formStatus.value = { type: 'error', msg: 'Please enter a valid email address.' };
    return;
  }

  formLoading.value = true;
  formStatus.value  = { type: '', msg: '' };

  try {
    const res = await fetch(WORKER_URL, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ name, email, service, message }),
    });

    if (res.ok) {
      formStatus.value = { type: 'success', msg: "Ticket submitted — I'll respond within one business day." };
      formName.value = formEmail.value = formService.value = formMessage.value = '';
    } else {
      const data = await res.json().catch(() => ({})) as { error?: string };
      formStatus.value = { type: 'error', msg: data.error ?? 'Something went wrong. Please try emailing directly.' };
    }
  } catch {
    formStatus.value = { type: 'error', msg: 'Network error — please email support@ridgecodetech.com directly.' };
  } finally {
    formLoading.value = false;
  }
}

onMounted(() => {
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          observer.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
  );
  document.querySelectorAll('.reveal').forEach((el) => observer.observe(el));
});
</script>

<style>
@keyframes fadeUp { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: none; } }

.hero { min-height: 100vh; background: var(--navy-deep); display: flex; flex-direction: column; justify-content: center; padding: 10rem 4rem 0; position: relative; overflow: hidden; }
.hero::before { content: ''; position: absolute; top: 0; left: 0; right: 0; bottom: 0; background: radial-gradient(ellipse 60% 50% at 70% 30%, rgba(122,150,193,0.1) 0%, transparent 70%), radial-gradient(ellipse 40% 40% at 20% 70%, rgba(15,38,79,0.8) 0%, transparent 60%); pointer-events: none; }
.hero-content { position: relative; max-width: 820px; }
.hero-eyebrow { font-family: 'Space Grotesk', sans-serif; font-size: 0.75rem; font-weight: 500; letter-spacing: 0.2em; text-transform: uppercase; color: var(--steel); margin-bottom: 1.5rem; opacity: 0; animation: fadeUp 0.7s 0.2s cubic-bezier(.4,0,.2,1) forwards; }
.hero-headline { font-family: 'Space Grotesk', sans-serif; font-size: clamp(2.8rem, 6vw, 5rem); font-weight: 700; line-height: 1.08; color: var(--white); letter-spacing: -0.02em; margin-bottom: 1.75rem; opacity: 0; animation: fadeUp 0.7s 0.4s cubic-bezier(.4,0,.2,1) forwards; }
.hero-headline em { font-style: normal; color: var(--periwinkle); }
.hero-sub { font-size: 1.15rem; font-weight: 300; color: var(--periwinkle); line-height: 1.7; max-width: 540px; margin-bottom: 3rem; opacity: 0; animation: fadeUp 0.7s 0.6s cubic-bezier(.4,0,.2,1) forwards; }
.hero-actions { display: flex; align-items: center; gap: 1.5rem; flex-wrap: wrap; opacity: 0; animation: fadeUp 0.7s 0.75s cubic-bezier(.4,0,.2,1) forwards; }

.ridge-divider { position: relative; width: 100%; height: 120px; background: var(--navy-deep); margin-top: 6rem; }
.ridge-divider svg { position: absolute; bottom: 0; left: 0; width: 100%; height: 100%; }

section { padding: 6rem 4rem; }
.section-inner { max-width: 1100px; margin: 0 auto; }
.section-label { font-family: 'Space Grotesk', sans-serif; font-size: 0.7rem; font-weight: 500; letter-spacing: 0.22em; text-transform: uppercase; color: var(--steel); margin-bottom: 1rem; }
.section-heading { font-family: 'Space Grotesk', sans-serif; font-size: clamp(1.75rem, 3vw, 2.5rem); font-weight: 700; letter-spacing: -0.02em; color: var(--navy); line-height: 1.2; margin-bottom: 1.5rem; }
.section-body { font-size: 1rem; font-weight: 300; color: var(--text-muted); line-height: 1.8; max-width: 620px; margin-bottom: 0; }

.services { background: var(--off-white); }
.services-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); gap: 2px; margin-top: 3.5rem; border: 2px solid var(--periwinkle); }
.service-card { padding: 2.5rem 2rem; background: var(--white); border-right: 2px solid var(--periwinkle); transition: background 0.2s; }
.service-card:last-child { border-right: none; }
.service-card:hover { background: var(--pale); }
.service-icon { width: 36px; height: 36px; margin-bottom: 1.25rem; opacity: 0.7; }
.service-title { font-family: 'Space Grotesk', sans-serif; font-size: 1rem; font-weight: 600; color: var(--navy); margin-bottom: 0.75rem; }
.service-desc { font-size: 0.875rem; line-height: 1.7; color: var(--text-muted); font-weight: 300; }

.hosting-section { background: var(--navy); padding: 6rem 4rem; }
.hosting-section .section-label { color: var(--steel); }
.hosting-section .section-heading { color: var(--pale); }
.hosting-section .section-body { color: var(--periwinkle); margin-bottom: 3rem; }
.hosting-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 2px; border: 2px solid var(--navy-mid); margin-top: 3rem; }
.hosting-card { background: rgba(255,255,255,0.03); border-right: 2px solid var(--navy-mid); padding: 2.5rem 2rem; display: flex; flex-direction: column; transition: background 0.2s; }
.hosting-card:last-child { border-right: none; }
.hosting-card:hover { background: rgba(122,150,193,0.06); }
.hosting-icon { width: 40px; height: 40px; margin-bottom: 1.25rem; opacity: 0.7; }
.hosting-title { font-family: 'Space Grotesk', sans-serif; font-size: 1rem; font-weight: 600; color: var(--pale); margin-bottom: 0.75rem; }
.hosting-desc { font-size: 0.875rem; line-height: 1.7; color: var(--periwinkle); font-weight: 300; margin-bottom: 1.5rem; }
.hosting-features { list-style: none; display: flex; flex-direction: column; gap: 0.5rem; margin-bottom: 1.75rem; flex: 1; }
.hosting-features li { font-size: 0.8rem; color: var(--steel); padding-left: 1rem; position: relative; line-height: 1.5; }
.hosting-features li::before { content: '—'; position: absolute; left: 0; opacity: 0.5; }
.hosting-tag { font-family: 'Space Grotesk', sans-serif; font-size: 0.65rem; font-weight: 500; letter-spacing: 0.1em; text-transform: uppercase; color: var(--steel); border: 1px solid var(--navy-mid); padding: 0.3rem 0.65rem; border-radius: 2px; width: fit-content; }
.hosting-note { margin-top: 2.5rem; font-size: 0.9rem; color: var(--periwinkle); font-weight: 300; }
.hosting-note a { color: var(--steel); text-decoration: none; border-bottom: 1px solid var(--navy-mid); padding-bottom: 1px; transition: color 0.2s, border-color 0.2s; }
.hosting-note a:hover { color: var(--pale); border-color: var(--steel); }

.about { background: var(--off-white); }
.about-layout { display: grid; grid-template-columns: 1fr 1fr; gap: 5rem; align-items: center; margin-top: 3rem; }
.about-text p { font-size: 1rem; line-height: 1.85; color: var(--text-muted); font-weight: 300; margin-bottom: 1.25rem; }
.about-text p:last-child { margin-bottom: 0; }
.about-stats { display: grid; grid-template-columns: 1fr 1fr; gap: 1.5px; background: var(--periwinkle); border: 1.5px solid var(--periwinkle); }
.stat-box { background: var(--white); padding: 2rem 1.75rem; transition: background 0.2s; }
.stat-box:hover { background: var(--pale); }
.stat-number { font-family: 'Space Grotesk', sans-serif; font-size: 2rem; font-weight: 700; color: var(--navy); letter-spacing: -0.03em; line-height: 1; margin-bottom: 0.5rem; }
.stat-label { font-size: 0.8rem; color: var(--text-muted); font-weight: 400; line-height: 1.4; }

.contact-section { background: var(--navy-deep); padding: 7rem 4rem; }
.contact-section .section-label { color: var(--steel); }
.contact-section .section-heading { color: var(--pale); }
.contact-layout { display: grid; grid-template-columns: 1fr 1.4fr; gap: 5rem; align-items: start; margin-top: 3rem; }
.contact-aside p { font-size: 0.9rem; color: var(--periwinkle); line-height: 1.8; font-weight: 300; margin-bottom: 2rem; }
.contact-detail { display: flex; flex-direction: column; gap: 0.75rem; }
.contact-detail a { font-family: 'Space Grotesk', sans-serif; font-size: 0.875rem; color: var(--steel); text-decoration: none; border-bottom: 1px solid var(--navy-mid); padding-bottom: 2px; width: fit-content; transition: color 0.2s, border-color 0.2s; }
.contact-detail a:hover { color: var(--pale); border-color: var(--steel); }
.contact-form { display: flex; flex-direction: column; gap: 1.25rem; }
.form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 1.25rem; }
.form-group { display: flex; flex-direction: column; gap: 0.4rem; }
.form-group label { font-family: 'Space Grotesk', sans-serif; font-size: 0.7rem; font-weight: 500; letter-spacing: 0.15em; text-transform: uppercase; color: var(--steel); }
.form-group input, .form-group select, .form-group textarea { font-family: 'Inter', sans-serif; font-size: 0.9rem; font-weight: 300; color: var(--pale); background: rgba(255,255,255,0.05); border: 1px solid var(--navy-mid); border-radius: 3px; padding: 0.8rem 1rem; outline: none; transition: border-color 0.2s, background 0.2s; width: 100%; }
.form-group select option { background: var(--navy-deep); color: var(--pale); }
.form-group input:focus, .form-group select:focus, .form-group textarea:focus { border-color: var(--steel); background: rgba(255,255,255,0.08); }
.form-group textarea { resize: vertical; min-height: 130px; }
.form-group input::placeholder, .form-group textarea::placeholder { color: var(--text-muted); }
.form-status { font-size: 0.875rem; padding: 0.75rem 1rem; border-radius: 3px; }
.form-status.success { background: rgba(122,150,193,0.15); border: 1px solid var(--steel); color: var(--pale); }
.form-status.error   { background: rgba(180,60,60,0.15); border: 1px solid #b44; color: #f5a0a0; }

@media (max-width: 900px) {
  .contact-layout { grid-template-columns: 1fr; gap: 3rem; }
  .about-layout   { grid-template-columns: 1fr; gap: 3rem; }
  .hosting-grid   { grid-template-columns: 1fr; }
  .hosting-card   { border-right: none; border-bottom: 2px solid var(--navy-mid); }
  .hosting-card:last-child { border-bottom: none; }
}
@media (max-width: 768px) {
  section { padding: 4rem 1.5rem; }
  .hero   { padding: 8rem 1.5rem 0; }
  .services-grid { grid-template-columns: 1fr; }
  .service-card  { border-right: none; border-bottom: 2px solid var(--periwinkle); }
  .service-card:last-child { border-bottom: none; }
  .form-row { grid-template-columns: 1fr; }
  .contact-section  { padding: 4rem 1.5rem; }
  .hosting-section  { padding: 4rem 1.5rem; }
}
</style>
